import { motion } from "framer-motion";
import { Button } from "@/components/ui/button";
import { Target, CheckCircle2, Brain, MessageSquare, Users, Wallet } from "lucide-react";
import {
  RadarChart,
  PolarGrid,
  PolarAngleAxis,
  Radar,
  ResponsiveContainer,
  Tooltip,
} from "recharts";
import { BlockScores, QuizProfile } from "./quizData";

interface QuizResultProps {
  totalScore: number;
  blockScores: BlockScores;
  profile: QuizProfile;
  onContinue: () => void;
}

// Helper to get radar fill color based on overall percentage
const getRadarColor = (percentage: number): { fill: string; stroke: string; status: string } => {
  if (percentage < 40) {
    return { fill: '#FB583A', stroke: '#FB583A', status: 'Crítico' }; // Red/Orange - Critical
  } else if (percentage <= 70) {
    return { fill: '#F59E0B', stroke: '#F59E0B', status: 'Alerta' }; // Amber - Alert
  } else {
    return { fill: '#A0C713', stroke: '#A0C713', status: 'Saudável' }; // Lime - Healthy
  }
};

// Custom tooltip component - simplified
const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: { subject: string; percentage: number } }> }) => {
  if (active && payload && payload.length) {
    const data = payload[0].payload;
    return (
      <div className="bg-white px-3 py-2 rounded-lg shadow-lg border border-border">
        <p className="font-semibold text-brand-dark text-sm">{data.subject}</p>
        <p className="text-muted-foreground text-xs">
          Pontuação: <span className="font-medium text-brand-orange">{data.percentage}%</span>
        </p>
      </div>
    );
  }
  return null;
};

const QuizResult = ({ totalScore, blockScores, profile, onContinue }: QuizResultProps) => {
  // Calculate overall percentage for status badge
  const overallPercentage = Math.round((totalScore / 50) * 100);
  const radarColors = getRadarColor(overallPercentage);
  
  // Total obtained points (sum of all blocks)
  const totalObtained = 
    blockScores.sobrecargaMental + 
    blockScores.comunicacao + 
    blockScores.delegacao + 
    blockScores.financeiro;

  // Radar data - each axis shows proportion of total obtained (sum = 100%)
  const radarData = [
    { 
      subject: 'Sobrecarga Mental', 
      percentage: totalObtained > 0 
        ? Math.round((blockScores.sobrecargaMental / totalObtained) * 100) 
        : 0,
      fullMark: 100 
    },
    { 
      subject: 'Eficiência na Comunicação', 
      percentage: totalObtained > 0 
        ? Math.round((blockScores.comunicacao / totalObtained) * 100) 
        : 0,
      fullMark: 100 
    },
    { 
      subject: 'Capacidade de Delegação', 
      percentage: totalObtained > 0 
        ? Math.round((blockScores.delegacao / totalObtained) * 100) 
        : 0,
      fullMark: 100 
    },
    { 
      subject: 'Inteligência Financeira', 
      percentage: totalObtained > 0 
        ? Math.round((blockScores.financeiro / totalObtained) * 100) 
        : 0,
      fullMark: 100 
    },
  ];


  const isStrategic = profile.id === 'estrategica';

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
      className="space-y-8"
    >
      {/* Header */}
      <div className="text-center space-y-4">
        <motion.div
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ delay: 0.2, type: "spring" }}
          className={`inline-flex items-center justify-center w-16 h-16 rounded-full ${
            isStrategic ? "bg-brand-lime/20" : "bg-brand-orange/20"
          }`}
        >
          {isStrategic ? (
            <CheckCircle2 className="w-8 h-8 text-brand-lime" />
          ) : (
            <Target className="w-8 h-8 text-brand-orange" />
          )}
        </motion.div>
        
        <div>
          <h2 className="text-2xl md:text-3xl font-bold text-brand-dark">
            Seu Perfil: {profile.title}
          </h2>
          <p className="text-lg text-muted-foreground mt-2">
            Pontuação: <span className="font-semibold text-brand-purple">{totalScore}/50</span>
          </p>
        </div>
        
        <p className="text-base text-muted-foreground max-w-lg mx-auto">
          {profile.description}
        </p>
      </div>

      {/* Radar Chart + Solutions Grid */}
      <div className="grid md:grid-cols-2 gap-6">
        {/* Radar Chart */}
        <motion.div 
          initial={{ opacity: 0, scale: 0.9 }}
          animate={{ opacity: 1, scale: 1 }}
          transition={{ delay: 0.3, duration: 0.5 }}
          className="bg-white rounded-2xl border border-border p-6"
        >
          <div className="flex items-center justify-between mb-4">
            <h3 className="text-lg font-semibold text-brand-dark">
              Seu Diagnóstico
            </h3>
            <span 
              className="text-xs font-medium px-2 py-1 rounded-full"
              style={{ 
                backgroundColor: `${radarColors.fill}20`,
                color: radarColors.fill 
              }}
            >
              {radarColors.status}
            </span>
          </div>
          
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <RadarChart data={radarData} cx="50%" cy="50%" outerRadius="65%">
                <PolarGrid 
                  stroke="#e2e8f0" 
                  gridType="polygon"
                />
                <PolarAngleAxis 
                  dataKey="subject" 
                  tick={{ 
                    fill: '#0B3041', 
                    fontSize: 10,
                    fontWeight: 500
                  }}
                  tickLine={false}
                />
                <Radar
                  name="Pontuação"
                  dataKey="percentage"
                  stroke={radarColors.stroke}
                  fill={radarColors.fill}
                  fillOpacity={0.3}
                  strokeWidth={2}
                  dot={{
                    r: 4,
                    fill: radarColors.fill,
                    stroke: '#fff',
                    strokeWidth: 2,
                  }}
                />
                <Tooltip content={<CustomTooltip />} />
              </RadarChart>
            </ResponsiveContainer>
          </div>

          {/* Legend - 4 Pillars */}
          <div className="grid grid-cols-2 gap-2 mt-4 pt-4 border-t border-border">
            {radarData.map((item, index) => {
              const icons = [Brain, MessageSquare, Users, Wallet];
              const Icon = icons[index];
              return (
                <div key={item.subject} className="flex items-center gap-2">
                  <Icon className="w-4 h-4 text-brand-navy" />
                  <div className="flex-1 min-w-0">
                    <p className="text-xs text-muted-foreground truncate">{item.subject}</p>
                    <p className="text-sm font-semibold text-brand-dark">{item.percentage}%</p>
                  </div>
                </div>
              );
            })}
          </div>
        </motion.div>

        {/* Solutions Card */}
        <div className="bg-white rounded-2xl border border-border p-6">
          <div className="flex items-center gap-2 mb-4">
            <Target className="w-5 h-5 text-brand-orange" />
            <h3 className="text-lg font-semibold text-brand-dark">
              Onde podemos te apoiar
            </h3>
          </div>
          
          <p className="text-sm text-muted-foreground mb-4">
            {profile.supportTitle}
          </p>
          
          <ul className="space-y-3">
            {profile.solutions.map((solution, index) => {
              const colonIndex = solution.indexOf(':');
              const hasColon = colonIndex !== -1;
              const prefix = hasColon ? solution.substring(0, colonIndex) : '';
              const suffix = hasColon ? solution.substring(colonIndex + 1) : solution;
              
              return (
                <motion.li
                  key={index}
                  initial={{ opacity: 0, x: -10 }}
                  animate={{ opacity: 1, x: 0 }}
                  transition={{ delay: 0.3 + index * 0.1 }}
                  className="flex items-start gap-3"
                >
                  <CheckCircle2 className="w-5 h-5 text-brand-lime shrink-0 mt-0.5" />
                  <span className="text-sm text-brand-dark">
                    {hasColon ? (
                      <>
                        <span className="font-bold">{prefix}:</span>
                        {suffix}
                      </>
                    ) : (
                      solution
                    )}
                  </span>
                </motion.li>
              );
            })}
          </ul>
        </div>
      </div>

      {/* CTA Button */}
      <motion.div
        initial={{ opacity: 0, y: 10 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ delay: 0.6 }}
        className="text-center"
      >
        <Button
          size="lg"
          onClick={onContinue}
          className="bg-brand-orange hover:bg-brand-orange/90 text-white rounded-full px-12 py-6 text-lg font-semibold shadow-lg shadow-brand-orange/25"
        >
          EU QUERO
        </Button>
      </motion.div>
    </motion.div>
  );
};

export default QuizResult;
